import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export interface ChildSummaryInput {
  learnerName: string;
  gradeLabel: string | null;
  attendancePct: number;
  attendanceWindow: string;
  recentScores: { subject: string; title: string; pct: number }[];
  pendingHomework: number;
  overdueHomework: number;
  recentIncidents: { category: string; severity: string; date: string }[];
  feeBalance: number;
  currency: string;
}

export const generateChildSummary = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: ChildSummaryInput) => d)
  .handler(async ({ data }) => {
    const key = process.env.LOVABLE_API_KEY;
    if (!key) return { text: "AI summary not configured.", error: true };

    const sys = "You are MAKAI, MAKAI's warm, concise parent assistant. Speak directly to the parent ('your child'). Output 3 short sections with bold headings: **How they're doing**, **What needs your attention**, **One thing to do this week**. Keep it under 130 words. Avoid emojis and jargon. Be specific using the data, encouraging but honest.";
    const user =
`Child: ${data.learnerName}
Class: ${data.gradeLabel ?? "n/a"}
Attendance (${data.attendanceWindow}): ${data.attendancePct.toFixed(0)}%
Homework: ${data.pendingHomework} pending, ${data.overdueHomework} overdue
Fee balance: ${data.currency} ${data.feeBalance.toLocaleString()}

Recent scores:
${data.recentScores.slice(0, 6).map((r) => `- ${r.subject} · ${r.title}: ${r.pct.toFixed(0)}%`).join("\n") || "- (none yet)"}

Behaviour notes (last 30 days):
${data.recentIncidents.slice(0, 4).map((i) => `- ${i.date}: ${i.category} (${i.severity})`).join("\n") || "- (clean record)"}`;

    try {
      const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Lovable-API-Key": key },
        body: JSON.stringify({
          model: "google/gemini-3-flash-preview",
          messages: [
            { role: "system", content: sys },
            { role: "user", content: user },
          ],
        }),
      });
      if (res.status === 429) return { text: "MAKAI is busy right now — try again in a moment.", error: true };
      if (res.status === 402) return { text: "AI credits exhausted. Ask your school admin.", error: true };
      if (!res.ok) return { text: `AI service error (${res.status}).`, error: true };
      const json = await res.json();
      const text = json?.choices?.[0]?.message?.content ?? "No summary generated.";
      return { text: String(text), error: false };
    } catch (e) {
      return { text: `AI request failed: ${(e as Error).message}`, error: true };
    }
  });
